import * as m from '@/paraglide/messages';
import { Schema } from 'effect';
import { getLocale } from '@/paraglide/runtime';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import {
  isMarkdownFile,
  MARKDOWN_FILE_ACCEPT,
  markdownToPlainText,
  markdownToSpeechDocument,
} from '@listening/utils/markdown';
import { cn } from '@listening/utils/react-helpers';
import {
  Code,
  Erase,
  Globe,
  LinkSlash,
  Page,
  PageUp,
  PasteClipboard,
  Redo,
  Search,
  Text,
  TextBox,
  Undo,
  WrapText,
  Xmark,
} from 'iconoir-react';
import { Input } from '@listening/frontend/components/radix/input';
import { Button } from '@listening/frontend/components/radix/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@listening/frontend/components/radix/form';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@listening/frontend/components/radix/tooltip';
import { Textarea } from '@listening/frontend/components/radix/textarea';
import {
  createFileRoute,
  useBlocker,
  useNavigate,
} from '@tanstack/react-router';
import { MobileNavDrawerButton } from '@/components/web-fe/design-v2/mobile-nav-drawer';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import { Spinner } from '@/components/shared-ui/spinner';
import { DashboardEditorPage } from '@/components/web-fe/pages/dashboard/dashboard-editor-page';
import {
  MAX_TEXT_LENGTH,
  VERY_SHORT_TEXT_LENGTH,
  SUPPORTED_TEXT_TO_AUDIO_LANGUAGES,
  countWords,
  deriveTextToAudioTitle,
  deriveTextUploadFilename,
  formatListeningTime,
  getTextToAudioLanguageName,
  makeTextToAudioSchema,
} from '@/components/web-fe/text-to-audio/text-to-audio-shared';
import { useLanguageDetection } from '@/components/web-fe/text-to-audio/use-language-detection';
import { useTextHistory } from '@/components/web-fe/text-to-audio/use-text-history';
import { audioService } from '@/services/audio-service';
import { useUploadDropzone } from '@/services/file-upload-service';

const formSchema = makeTextToAudioSchema({ includeTitle: true });

type FormValues = typeof formSchema.Type;

function escapeRegExp(value: string) {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function truncateAtWordBoundary(text: string, maxChars: number) {
  if (text.length <= maxChars) return text;

  const clipped = text.slice(0, maxChars + 1);
  const lastSpace = clipped.lastIndexOf(' ');
  const boundary =
    lastSpace >= Math.floor(maxChars * 0.6) ? lastSpace : maxChars;

  return clipped.slice(0, boundary).trim();
}

function deriveDescription(title: string | undefined, text: string) {
  const normalizedTitle = title?.trim();
  const normalizedText = text.trim().replaceAll(/\s+/g, ' ');
  if (!normalizedText) return normalizedTitle || undefined;

  const excerpt = normalizedText.split(' ').slice(0, 32).join(' ');
  return truncateAtWordBoundary(excerpt, 220) || normalizedTitle || undefined;
}

function isPlainTextFile(file: File) {
  return file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt');
}

export const Route = createFileRoute(
  '/app/_authenticated/_dashboard/text-to-audio',
)({
  component: TextToAudioPage,
  context: () => ({
    title: m.dialog_text_to_audio_editor_title(),
    icon: TextBox,
  }),
});

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          className='flex size-8 items-center justify-center rounded-md text-[var(--v2-text-muted)] transition-all hover:bg-black/[0.06] hover:text-[var(--v2-text-primary)] active:scale-95 disabled:pointer-events-none disabled:opacity-30 dark:hover:bg-white/[0.08] dark:hover:text-white'
        >
          <Icon className='size-[15px]' />
        </button>
      </TooltipTrigger>
      <TooltipContent side='bottom' className='text-xs'>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function ToolbarDivider() {
  return (
    <div className='mx-1.5 h-4 w-px bg-black/[0.08] dark:bg-white/[0.08]' />
  );
}

function StatusBarItem({ label, value }: { label: string; value: string }) {
  return (
    <span className='text-[12px] text-[var(--v2-text-muted)] tabular-nums dark:text-[var(--v2-text-muted)]'>
      <span className='text-[var(--v2-text-muted)]'>{value}</span> {label}
    </span>
  );
}

function StatusBarUploadButton({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) {
  const label = m.dialog_text_to_audio_upload_text();

  return (
    <button
      type='button'
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className='inline-flex h-8 items-center gap-1.5 rounded-md border border-black/[0.08] bg-white/60 px-2.5 text-[12px] font-medium text-[var(--v2-text-muted)] transition hover:border-[var(--v2-accent)]/25 hover:bg-[var(--v2-accent)]/[0.07] hover:text-[var(--v2-text-primary)] disabled:pointer-events-none disabled:opacity-40 dark:border-white/[0.10] dark:bg-white/[0.04] dark:hover:border-[var(--v2-accent)]/30 dark:hover:bg-[var(--v2-accent)]/[0.12]'
    >
      <PageUp className='size-3.5' />
      <span>{label}</span>
    </button>
  );
}

function TextToAudioPage() {
  const navigate = useNavigate();
  const locale = getLocale();
  const isSubmittingRef = useRef(false);
  const skipUnsavedContentBlockRef = useRef(false);
  const pendingTitleRef = useRef<string | undefined>(undefined);
  const pendingDescriptionRef = useRef<string | undefined>(undefined);
  const { onDrop, uploadMutation } = useUploadDropzone({
    showInProgressToast: false,
    location: 'text_to_audio_page',
    openShareDialogOnSuccess: false,
    getCreateAudioInput: () => ({
      title: pendingTitleRef.current,
    }),
  });
  const updateAudioMutation = audioService.mutations.useUpdateAudioMutation();

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(
      Schema.toStandardSchemaV1(formSchema),
    ) as Resolver<FormValues>,
    defaultValues: {
      title: '',
      text: '',
    },
  });

  const [showShortTextWarning, setShowShortTextWarning] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');

  const textValue = useWatch({ control: form.control, name: 'text' });
  const titleValue = useWatch({ control: form.control, name: 'title' }) ?? '';
  const trimmedText = textValue.trim();
  const trimmedTextLength = trimmedText.length;
  const hasUnsavedContent =
    trimmedTextLength > 0 || titleValue.trim().length > 0;
  const isVeryShortText =
    trimmedTextLength > 0 && trimmedTextLength <= VERY_SHORT_TEXT_LENGTH;
  const [warningTextValue, setWarningTextValue] = useState(textValue);
  if (warningTextValue !== textValue) {
    setWarningTextValue(textValue);
    setShowShortTextWarning(false);
  }

  const langDetection = useLanguageDetection(textValue);
  const isUnsupportedLanguage = langDetection.status === 'unsupported';
  const detectedLanguageName =
    langDetection.status === 'idle'
      ? null
      : getTextToAudioLanguageName(langDetection.lang, locale);
  const supportedLanguageList = SUPPORTED_TEXT_TO_AUDIO_LANGUAGES.map((lang) =>
    getTextToAudioLanguageName(lang, locale),
  ).join(', ');
  const isSubmitDisabled = uploadMutation.isPending || isUnsupportedLanguage;
  const submitButtonLabel =
    showShortTextWarning && isVeryShortText
      ? m.dialog_text_to_audio_continue_anyway()
      : m.dialog_convert_to_audio();

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const findInputRef = useRef<HTMLInputElement | null>(null);
  const markdownFileInputRef = useRef<HTMLInputElement | null>(null);
  const dragCounterRef = useRef(0);

  const { canRedo, canUndo, handleRedo, handleUndo, setText } = useTextHistory({
    form,
    textValue,
    textareaRef,
  });

  const clearSuccessfulSubmission = useCallback(() => {
    skipUnsavedContentBlockRef.current = true;
    isSubmittingRef.current = false;
    pendingTitleRef.current = undefined;
    pendingDescriptionRef.current = undefined;
    form.reset({
      title: '',
      text: '',
    });
  }, [form]);

  const stats = useMemo(() => {
    const words = countWords(textValue);
    const characters = trimmedTextLength;
    const listeningTime = formatListeningTime(words);

    return {
      characters,
      listeningTime,
      words,
    };
  }, [textValue, trimmedTextLength]);

  const submitText = useCallback(
    async (title: string | undefined, text: string) => {
      if (isUnsupportedLanguage) return;

      skipUnsavedContentBlockRef.current = false;
      pendingTitleRef.current = deriveTextToAudioTitle({
        title,
        text,
      });
      pendingDescriptionRef.current = deriveDescription(title, text);
      const file = new File(
        [text],
        deriveTextUploadFilename({
          text,
          title,
        }),
      );

      const startedUpload = await onDrop([file]);
      isSubmittingRef.current = startedUpload;
      if (!startedUpload) {
        pendingTitleRef.current = undefined;
        pendingDescriptionRef.current = undefined;
      }
    },
    [isUnsupportedLanguage, onDrop],
  );

  useEffect(() => {
    if (!isSubmittingRef.current) return;

    if (uploadMutation.isError) {
      isSubmittingRef.current = false;
      skipUnsavedContentBlockRef.current = false;
      pendingTitleRef.current = undefined;
      pendingDescriptionRef.current = undefined;
      return;
    }

    if (!uploadMutation.isSuccess) return;

    const audioId = uploadMutation.data?.[0]?.audioConversion.id;
    const description = pendingDescriptionRef.current;

    clearSuccessfulSubmission();

    if (audioId && description) {
      updateAudioMutation.mutate({
        audioId,
        updates: { description },
      });
    }

    void navigate({
      replace: true,
      to: '/app/home',
    }).catch(() => {
      skipUnsavedContentBlockRef.current = false;
    });
  }, [
    clearSuccessfulSubmission,
    navigate,
    updateAudioMutation,
    uploadMutation.data,
    uploadMutation.isError,
    uploadMutation.isSuccess,
  ]);

  const handlePasteFromClipboard = useCallback(async () => {
    const text = await navigator.clipboard.readText().catch(() => undefined);
    if (!text) return;

    const current = form.getValues('text');
    const element = textareaRef.current;
    if (!element) return;

    const nextText =
      current.slice(0, element.selectionStart) +
      text +
      current.slice(element.selectionEnd);

    setText(nextText, { focus: true });
  }, [form, setText]);

  const handleCleanWhitespace = useCallback(() => {
    const cleaned = form
      .getValues('text')
      .replaceAll(/[^\S\n]+/g, ' ')
      .replaceAll(/\n{3,}/g, '\n\n')
      .replaceAll(/^ +| +$/gm, '')
      .trim();

    setText(cleaned);
  }, [form, setText]);

  const handleStripUrls = useCallback(() => {
    const cleaned = form
      .getValues('text')
      .replaceAll(/https?:\/\/[^\s)>\]]+/g, '')
      .replaceAll(/ {2,}/g, ' ')
      .replaceAll(/^ +| +$/gm, '');

    setText(cleaned);
  }, [form, setText]);

  const handleJoinBrokenLines = useCallback(() => {
    const cleaned = form
      .getValues('text')
      .replaceAll('\r\n', '\n')
      .replaceAll(/\n{2,}/g, '\n\n')
      .split('\n\n')
      .map((paragraph) =>
        paragraph.replaceAll('\n', ' ').replaceAll(/ {2,}/g, ' ').trim(),
      )
      .join('\n\n');

    setText(cleaned);
  }, [form, setText]);

  const handleStripMarkup = useCallback(() => {
    setText(markdownToPlainText(form.getValues('text')));
  }, [form, setText]);

  const handleClear = useCallback(() => {
    form.setValue('title', '', { shouldDirty: true });
    setText('', { focus: true });
  }, [form, setText]);

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const importTextFile = useCallback(
    async (file: File) => {
      const content = await file.text();
      const importedDocument = isMarkdownFile(file)
        ? markdownToSpeechDocument(content)
        : { text: content };
      const current = form.getValues('text');

      if (
        'title' in importedDocument &&
        importedDocument.title &&
        !(form.getValues('title') ?? '').trim()
      ) {
        form.setValue('title', importedDocument.title, {
          shouldDirty: true,
        });
      }

      setText(
        current
          ? `${current}\n\n${importedDocument.text}`
          : importedDocument.text,
        {
          focus: true,
        },
      );
    },
    [form, setText],
  );

  const handleMarkdownFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !isMarkdownFile(file)) return;

      void importTextFile(file);
    },
    [importTextFile],
  );

  const handleChooseMarkdownFile = useCallback(() => {
    markdownFileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      dragCounterRef.current = 0;
      setIsDragOver(false);

      const file = event.dataTransfer.files[0];
      if (file && (isPlainTextFile(file) || isMarkdownFile(file))) {
        await importTextFile(file);
        return;
      }

      const droppedText = event.dataTransfer.getData('text/plain');
      if (droppedText) {
        const current = form.getValues('text');
        setText(current ? `${current}\n\n${droppedText}` : droppedText, {
          focus: true,
        });
      }
    },
    [form, importTextFile, setText],
  );

  const matchCount = useMemo(() => {
    if (!findText) return 0;
    return (textValue.match(new RegExp(escapeRegExp(findText), 'gi')) ?? [])
      .length;
  }, [findText, textValue]);

  const handleToggleFindReplace = useCallback(() => {
    setShowFindReplace((previous) => {
      if (!previous) {
        requestAnimationFrame(() => findInputRef.current?.focus());
      }
      return !previous;
    });
  }, []);

  const handleReplaceNext = useCallback(() => {
    if (!findText) return;

    const replaced = form
      .getValues('text')
      .replace(new RegExp(escapeRegExp(findText), 'i'), replaceText);

    setText(replaced);
  }, [findText, form, replaceText, setText]);

  const handleReplaceAll = useCallback(() => {
    if (!findText) return;

    const replaced = form
      .getValues('text')
      .replaceAll(new RegExp(escapeRegExp(findText), 'gi'), replaceText);

    setText(replaced);
  }, [findText, form, replaceText, setText]);

  useEffect(() => {
    if (!hasUnsavedContent) return;

    const handler = (event: BeforeUnloadEvent) => {
      if (isSubmittingRef.current || skipUnsavedContentBlockRef.current) {
        return;
      }

      event.preventDefault();
      // oxlint-disable-next-line typescript/no-deprecated -- Chromium < 119 only prompts when returnValue is set
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedContent]);

  const blocker = useBlocker({
    shouldBlockFn: () =>
      hasUnsavedContent &&
      !isSubmittingRef.current &&
      !skipUnsavedContentBlockRef.current,
    withResolver: true,
  });

  return (
    <DashboardEditorPage>
      <Dialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) {
            blocker.reset?.();
          }
        }}
      >
        <DialogContent className='max-w-sm rounded-xl border-black/[0.08] bg-white p-6 dark:border-white/[0.08] dark:bg-[var(--v2-card-bg)]'>
          <DialogHeader>
            <DialogTitle className='text-base font-semibold text-[var(--v2-text-primary)]'>
              {m.dialog_text_to_audio_unsaved_title()}
            </DialogTitle>
            <DialogDescription className='mt-1.5 text-[13px] leading-relaxed text-[var(--v2-text-muted)]'>
              {m.dialog_text_to_audio_unsaved_desc()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='mt-2 flex gap-2 sm:gap-2'>
            <Button
              type='button'
              variant='ghost'
              onClick={() => blocker.reset?.()}
              className='flex-1 rounded-lg text-[13px] font-medium'
            >
              {m.dialog_text_to_audio_keep_editing()}
            </Button>
            <Button
              type='button'
              onClick={() => blocker.proceed?.()}
              className='flex-1 rounded-lg bg-red-500 text-[13px] font-medium text-white hover:bg-red-600'
            >
              {m.dialog_text_to_audio_leave_page()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Form {...form}>
        <form
          className='flex h-full flex-col'
          onSubmit={(event) =>
            void form.handleSubmit(({ title, text }) => {
              if (isVeryShortText && !showShortTextWarning) {
                setShowShortTextWarning(true);
                return;
              }

              void submitText(title, text);
            })(event)
          }
        >
          <div className='sticky top-0 z-10 shrink-0 border-b border-black/[0.06] bg-white dark:border-white/[0.06] dark:bg-[var(--v2-card-bg)]'>
            <div className='flex items-center justify-between px-4 py-1.5'>
              <div className='flex items-center gap-0.5'>
                {/* The shell's top bar is suppressed on this route and the
                    sidebar is hidden below lg, so without this the editor has
                    no way back to the rest of the app on a phone. */}
                <MobileNavDrawerButton className='mr-1 lg:hidden' />
                <ToolbarButton
                  icon={Undo}
                  label={m.dialog_text_to_audio_undo()}
                  onClick={handleUndo}
                  disabled={!canUndo}
                />
                <ToolbarButton
                  icon={Redo}
                  label={m.dialog_text_to_audio_redo()}
                  onClick={handleRedo}
                  disabled={!canRedo}
                />
                <ToolbarDivider />
                <ToolbarButton
                  icon={PasteClipboard}
                  label={m.dialog_text_to_audio_paste_from_clipboard()}
                  onClick={handlePasteFromClipboard}
                />
                <ToolbarButton
                  icon={Text}
                  label={m.dialog_text_to_audio_clean_whitespace()}
                  onClick={handleCleanWhitespace}
                  disabled={trimmedTextLength === 0}
                />
                <ToolbarDivider />
                <ToolbarButton
                  icon={LinkSlash}
                  label={m.dialog_text_to_audio_strip_urls()}
                  onClick={handleStripUrls}
                  disabled={trimmedTextLength === 0}
                />
                <ToolbarButton
                  icon={WrapText}
                  label={m.dialog_text_to_audio_join_broken_lines()}
                  onClick={handleJoinBrokenLines}
                  disabled={trimmedTextLength === 0}
                />
                <ToolbarButton
                  icon={Code}
                  label={m.dialog_text_to_audio_strip_markup()}
                  onClick={handleStripMarkup}
                  disabled={trimmedTextLength === 0}
                />
                <ToolbarDivider />
                <ToolbarButton
                  icon={Search}
                  label={m.dialog_text_to_audio_find_replace()}
                  onClick={handleToggleFindReplace}
                />
                <ToolbarDivider />
                <ToolbarButton
                  icon={Erase}
                  label={m.dialog_text_to_audio_clear_all()}
                  onClick={handleClear}
                  disabled={!hasUnsavedContent}
                />
              </div>

              <Button
                className='flex h-8 items-center gap-2 rounded-[var(--v2-button-radius)] bg-[var(--v2-cta-bg)] px-4 text-[13px] font-semibold text-[var(--v2-cta-text)] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50'
                disabled={isSubmitDisabled}
                type='submit'
              >
                {uploadMutation.isPending && <Spinner className='size-3.5' />}
                {submitButtonLabel}
              </Button>
            </div>

            {showFindReplace ? (
              <div className='flex items-center gap-2 border-t border-black/[0.04] px-4 py-2 dark:border-white/[0.04]'>
                <div className='flex flex-1 items-center gap-2'>
                  <div className='relative flex-1'>
                    <Input
                      ref={findInputRef}
                      value={findText}
                      onChange={(event) => setFindText(event.target.value)}
                      placeholder={m.dialog_text_to_audio_find_placeholder()}
                      className='h-7 rounded-md border-black/[0.08] bg-white pr-12 text-[13px] dark:border-white/[0.08] dark:bg-[var(--v2-card-bg)]'
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          setShowFindReplace(false);
                        }
                      }}
                    />
                    {findText ? (
                      <span className='absolute top-1/2 right-2 -translate-y-1/2 text-[11px] text-[var(--v2-text-muted)] tabular-nums'>
                        {matchCount}
                      </span>
                    ) : null}
                  </div>
                  <Input
                    value={replaceText}
                    onChange={(event) => setReplaceText(event.target.value)}
                    placeholder={m.dialog_text_to_audio_replace_placeholder()}
                    className='h-7 flex-1 rounded-md border-black/[0.08] bg-white text-[13px] dark:border-white/[0.08] dark:bg-[var(--v2-card-bg)]'
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        setShowFindReplace(false);
                      }
                      if (event.key === 'Enter') {
                        handleReplaceNext();
                      }
                    }}
                  />
                </div>
                <div className='flex items-center gap-1'>
                  <Button
                    type='button'
                    variant='ghost'
                    onClick={handleReplaceNext}
                    disabled={!findText || matchCount === 0}
                    className='h-7 rounded-md px-2.5 text-[12px] font-medium'
                  >
                    {m.dialog_text_to_audio_replace_next()}
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    onClick={handleReplaceAll}
                    disabled={!findText || matchCount === 0}
                    className='h-7 rounded-md px-2.5 text-[12px] font-medium'
                  >
                    {m.dialog_text_to_audio_replace_all()}
                  </Button>
                  <button
                    type='button'
                    aria-label={m.common_close()}
                    onClick={() => setShowFindReplace(false)}
                    className='flex size-7 items-center justify-center rounded-md text-[var(--v2-text-muted)] hover:bg-black/[0.06] hover:text-[var(--v2-text-primary)] dark:hover:bg-white/[0.08] dark:hover:text-white'
                  >
                    <Xmark className='size-3.5' />
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className='flex min-h-0 flex-1 bg-[var(--v2-app-bg)]'>
            <input
              ref={markdownFileInputRef}
              type='file'
              accept={MARKDOWN_FILE_ACCEPT}
              className='hidden'
              onChange={handleMarkdownFileSelect}
            />
            <div className='mx-auto flex w-full max-w-[816px] flex-col px-6 py-8 sm:py-12'>
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={cn(
                  'relative flex min-h-0 flex-1 flex-col rounded-sm border bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.03)] transition-shadow duration-200 dark:bg-[var(--v2-card-bg)]',
                  'border-black/[0.10] dark:border-white/[0.10]',
                  isDragOver &&
                    'border-[var(--v2-accent)]/40 shadow-[0_0_0_2px_rgb(0_0_0/0.04),0_4px_16px_rgb(0_0_0/0.06)]',
                )}
              >
                {isDragOver ? (
                  <div className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-sm bg-[var(--v2-accent)]/[0.04]'>
                    <div className='flex flex-col items-center gap-2 rounded-xl bg-white/90 px-6 py-4 shadow-lg dark:bg-[var(--v2-card-bg)]/90'>
                      <Page className='size-6 text-[var(--v2-accent)]' />
                      <p className='text-sm font-medium text-[var(--v2-accent)]'>
                        {m.dialog_text_to_audio_drop_text_here()}
                      </p>
                    </div>
                  </div>
                ) : null}

                <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
                  <FormField
                    control={form.control}
                    name='title'
                    render={({ field }) => (
                      <FormItem className='shrink-0'>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            type='text'
                            maxLength={100}
                            placeholder={m.dialog_text_to_audio_title_placeholder()}
                            disabled={uploadMutation.isPending}
                            onPaste={(event) => {
                              event.preventDefault();
                              const pasted = event.clipboardData
                                .getData('text/plain')
                                .replaceAll(/[\r\n]+/g, ' ')
                                .trim()
                                .slice(0, 100);
                              field.onChange(pasted);
                            }}
                            className='h-auto min-h-[72px] w-full rounded-none border-0 bg-transparent px-12 pt-10 pb-0 text-[22px] leading-tight font-semibold text-[var(--v2-text-primary)] outline-none placeholder:text-[var(--v2-text-muted)]/40 sm:min-h-[80px] sm:px-16 sm:pt-12 dark:text-white'
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='text'
                    render={({ field }) => (
                      <FormItem className='flex min-h-0 flex-1 flex-col space-y-0'>
                        <FormControl>
                          <Textarea
                            {...field}
                            ref={(element) => {
                              field.ref(element);
                              textareaRef.current = element;
                            }}
                            className='min-h-0 flex-1 resize-none overflow-y-auto rounded-none border-none bg-transparent px-12 pt-4 pb-6 font-[system-ui] text-[16px] leading-[1.9] tracking-[0.01em] text-[var(--v2-text-primary)] shadow-none ring-0 ring-offset-0 placeholder:text-[var(--v2-text-muted)]/60 focus-visible:ring-0 focus-visible:ring-offset-0 sm:px-16 dark:text-white'
                            disabled={uploadMutation.isPending}
                            maxLength={MAX_TEXT_LENGTH}
                            placeholder={m.dialog_text_to_audio_placeholder()}
                            autoFocus
                          />
                        </FormControl>
                        <FormMessage className='px-12 pb-4 sm:px-16' />
                      </FormItem>
                    )}
                  />
                </div>

                <div className='flex shrink-0 items-center justify-between border-t border-black/[0.04] px-12 py-2.5 sm:px-16 dark:border-white/[0.04]'>
                  <div className='flex items-center gap-4'>
                    <StatusBarUploadButton
                      onClick={handleChooseMarkdownFile}
                      disabled={uploadMutation.isPending}
                    />
                    {trimmedTextLength > 0 ? (
                      <>
                        <StatusBarItem
                          label={
                            stats.words === 1
                              ? m.dialog_text_to_audio_word_label()
                              : m.dialog_text_to_audio_words_label()
                          }
                          value={stats.words.toLocaleString(locale)}
                        />
                        <StatusBarItem
                          label={
                            stats.characters === 1
                              ? m.dialog_text_to_audio_character_label()
                              : m.dialog_text_to_audio_characters_label()
                          }
                          value={stats.characters.toLocaleString(locale)}
                        />
                      </>
                    ) : null}
                  </div>
                  <div className='flex items-center gap-4'>
                    {langDetection.status !== 'idle' && detectedLanguageName ? (
                      <span
                        className={cn(
                          'flex items-center gap-1 text-[12px]',
                          langDetection.status === 'unsupported'
                            ? 'text-red-500 dark:text-red-400'
                            : 'text-[var(--v2-text-muted)]',
                        )}
                      >
                        <Globe className='size-3' />
                        {detectedLanguageName}
                      </span>
                    ) : null}
                    {stats.listeningTime ? (
                      <StatusBarItem
                        label={m.dialog_text_to_audio_listening_time_label()}
                        value={stats.listeningTime}
                      />
                    ) : null}
                  </div>
                </div>
              </div>

              <Button
                className='mt-4 h-11 shrink-0 gap-2 rounded-[var(--v2-button-radius)] bg-[var(--v2-cta-bg)] text-sm font-semibold text-[var(--v2-cta-text)] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50'
                disabled={isSubmitDisabled}
                type='submit'
              >
                {uploadMutation.isPending && (
                  <Spinner className='size-4 shrink-0' />
                )}
                {submitButtonLabel}
              </Button>

              {isUnsupportedLanguage && detectedLanguageName ? (
                <div className='mt-4 shrink-0 rounded-lg border border-red-200/80 bg-red-50/90 px-5 py-3.5 shadow-sm dark:border-red-400/20 dark:bg-red-950/80'>
                  <p className='text-sm font-semibold text-red-900 dark:text-red-200'>
                    {m.dialog_text_to_audio_language_not_supported_title()}
                  </p>
                  <p className='mt-0.5 text-[13px] leading-relaxed text-red-800 dark:text-red-300/80'>
                    {m.dialog_text_to_audio_language_not_supported_desc({
                      language: detectedLanguageName,
                      supported_languages: supportedLanguageList,
                    })}
                  </p>
                </div>
              ) : null}

              {showShortTextWarning &&
              isVeryShortText &&
              !isUnsupportedLanguage ? (
                <div className='mt-4 shrink-0 rounded-lg border border-amber-200/80 bg-amber-50/90 px-5 py-3.5 shadow-sm dark:border-amber-400/20 dark:bg-amber-950/80'>
                  <p className='text-sm font-semibold text-amber-900 dark:text-amber-200'>
                    {m.dialog_text_to_audio_short_warning_title()}
                  </p>
                  <p className='mt-0.5 text-[13px] leading-relaxed text-amber-800 dark:text-amber-300/80'>
                    {m.dialog_text_to_audio_short_warning_desc({
                      count: trimmedTextLength.toString(),
                    })}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </form>
      </Form>
    </DashboardEditorPage>
  );
}
